import React, { useRef } from 'react'
import { Popover } from 'antd'
import moment from 'moment'
import classNames from 'classnames'
import useIknow from '@/hooks/useIknow'
import { useInViewport } from 'ahooks'

import styles from './index.less'

export const Content = ({ onOk, text, btnText = '我知道了', stopPropagation, contentContainerClassname }) => {
    return (
        <div
            className={classNames(styles.tipBox, contentContainerClassname)}
            onClick={e => {
                if (stopPropagation) {
                    e.stopPropagation()
                }
            }}
        >
            <span className={styles.tipText}>{text}</span>
            <div
                onClick={e => {
                    if (stopPropagation) {
                        e.stopPropagation()
                    }
                    onOk(e)
                }}
                className={styles.know}
            >
                <a>{btnText}</a>
            </div>
        </div>
    )
}

export const PopOverContent = ({
    children,
    setRender,
    sort,
    zIndex = 890,
    disabled = false,
    customizeParent,
    autoAdjustOverflow = true,
    name,
    placement = 'top',
    text,
    triggerClose,
    btnText,
    stopPropagation = false, // 点击 按钮是否阻止事件冒泡，默认不阻止
    delayLoading = false, // 某些业务下，由于需要拉接口，iknow包裹的组件位置会变化，所以这时候气泡位置会不对，这里是需要等待的接口的loading
    contentContainerClassname,
    isInViewport, // popover的children元素是否进入可视区域
    ...props
}) => {
    const { status, saveStatus } = useIknow({ name, setRender, sort })

    const cloneChildren = triggerClose
        ? React.cloneElement(children, {
              onClick(e) {
                  if (status) {
                      saveStatus()
                  }
                  children?.props?.onClick && children.props.onClick(e)
              }
          })
        : children

    return (
        <Popover
            zIndex={zIndex}
            placement={placement}
            visible={!delayLoading && status && !disabled && isInViewport}
            autoAdjustOverflow={autoAdjustOverflow}
            arrowPointAtCenter
            getPopupContainer={triggerNode =>
                // eslint-disable-next-line no-nested-ternary
                customizeParent
                    ? typeof customizeParent === 'function'
                        ? customizeParent(triggerNode)
                        : customizeParent
                    : triggerNode.parentElement
            }
            content={
                <Content
                    text={text}
                    onOk={saveStatus}
                    btnText={btnText}
                    stopPropagation={stopPropagation}
                    contentContainerClassname={contentContainerClassname}
                />
            }
            {...props}
        >
            {cloneChildren}
        </Popover>
    )
}

let nowDate = null

const isOverEndTimesObj = {}

export const getIsOverEndTimes = endTime => {
    if (!endTime) {
        return true
    }

    if (isOverEndTimesObj.hasOwnProperty(endTime)) {
        return isOverEndTimesObj[endTime]
    }

    if (!nowDate) {
        nowDate = moment().format('YYYY-MM-DD')
    }

    let diffDays = 1
    diffDays = moment(endTime).diff(moment(nowDate), 'days')
    if (diffDays <= 0) {
        isOverEndTimesObj[endTime] = false
        return false
    } else {
        isOverEndTimesObj[endTime] = true
        return true
    }
}

// 默认options参数 https://ahooks.js.org/zh-CN/hooks/use-in-viewport
const defaultInViewportOption = {
    threshold: [1], // 可以是单一的 number 也可以是 number 数组，target 元素和 root 元素相交程度达到该值的时候 ratio 会被更新，默认为全部进入视口
    root: null, // 指定根(root)元素，用于检查目标的可见性。必须是目标元素的父级元素，如果未指定或者为 null，则默认为浏览器视窗
    rootMargin: '0px' // 根(root)元素的外边距 默认上下左右均为0 扩展视口范围（正值）：让元素在真正进入视口之前就被检测到，缩小视口范围（负值）：需要元素进入视口更深一些才被检测到
}

const PopOverContentWrap = React.memo(({ endTime, children, inViewportOption, ...restProps }) => {
    const popOverWrapRef = useRef(null)

    const [inViewport] = useInViewport(popOverWrapRef, Object.assign(defaultInViewportOption, inViewportOption || {}))

    const isOverEndTimes = getIsOverEndTimes(endTime)

    if (!isOverEndTimes) {
        return children
    }

    return (
        <div ref={popOverWrapRef}>
            <PopOverContent {...restProps} isInViewport={inViewport}>
                {children}
            </PopOverContent>
        </div>
    )
})

export default PopOverContentWrap
